/**
 * Student Chat Conversation History Service
 * Manages conversation storage, retrieval, and operations using localStorage
 * Isolated namespace for /student-chat page
 */

const CHAT_ID = 'student_chat';
const STORAGE_KEY = `chat_history_${CHAT_ID}`;
const CURRENT_CONVERSATION_KEY = `chat_current_${CHAT_ID}`;

/**
 * Generate automatic title from first user message
 * @param {string} firstMessage - First user message in conversation
 * @returns {string} Generated title (max 50 chars)
 */
export const generateConversationTitle = (firstMessage) => {
  if (!firstMessage) return 'Nova Conversa';
  
  // Truncate and clean the message for title
  const cleanMessage = firstMessage?.trim()?.replace(/\n/g, ' ');
  const maxLength = 50;
  
  if (cleanMessage?.length <= maxLength) {
    return cleanMessage;
  }
  
  return cleanMessage?.substring(0, maxLength - 3) + '...';
};

/**
 * Get all saved conversations
 * @returns {Array} Array of conversation objects
 */
export const getAllConversations = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading conversations:', error);
    return [];
  }
};

/**
 * Save a new conversation or update existing one
 * @param {Object} conversation - Conversation object with id, title, messages, timestamp
 * @returns {Object} Saved conversation
 */
export const saveConversation = (conversation) => {
  try {
    const conversations = getAllConversations();
    const existingIndex = conversations?.findIndex(c => c?.id === conversation?.id);
    
    if (existingIndex >= 0) {
      // Update existing conversation
      conversations[existingIndex] = {
        ...conversations?.[existingIndex],
        ...conversation,
        updatedAt: new Date()?.toISOString()
      };
    } else {
      // Add new conversation
      conversations?.unshift({
        ...conversation,
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      });
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
    return conversation;
  } catch (error) {
    console.error('Error saving conversation:', error);
    throw error;
  }
};

/**
 * Get a specific conversation by ID
 * @param {string} conversationId - Conversation ID
 * @returns {Object|null} Conversation object or null if not found
 */
export const getConversation = (conversationId) => {
  try {
    const conversations = getAllConversations();
    return conversations?.find(c => c?.id === conversationId) || null;
  } catch (error) {
    console.error('Error getting conversation:', error);
    return null;
  }
};

/**
 * Delete a conversation by ID
 * @param {string} conversationId - Conversation ID to delete
 * @returns {boolean} Success status
 */
export const deleteConversation = (conversationId) => {
  try {
    const conversations = getAllConversations();
    const filtered = conversations?.filter(c => c?.id !== conversationId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    
    // Clear current conversation if it was deleted
    const currentId = getCurrentConversationId();
    if (currentId === conversationId) {
      clearCurrentConversation();
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting conversation:', error);
    return false;
  }
};

/**
 * Rename a conversation
 * @param {string} conversationId - Conversation ID
 * @param {string} newTitle - New title for the conversation
 * @returns {boolean} Success status
 */
export const renameConversation = (conversationId, newTitle) => {
  try {
    const conversations = getAllConversations();
    const conversation = conversations?.find(c => c?.id === conversationId);
    
    if (conversation) {
      conversation.title = newTitle;
      conversation.updatedAt = new Date()?.toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error renaming conversation:', error);
    return false;
  }
};

/**
 * Set current active conversation ID
 * @param {string} conversationId - Conversation ID to set as current
 */
export const setCurrentConversationId = (conversationId) => {
  try {
    localStorage.setItem(CURRENT_CONVERSATION_KEY, conversationId);
  } catch (error) {
    console.error('Error setting current conversation:', error);
  }
};

/**
 * Get current active conversation ID
 * @returns {string|null} Current conversation ID or null
 */
export const getCurrentConversationId = () => {
  try {
    return localStorage.getItem(CURRENT_CONVERSATION_KEY);
  } catch (error) {
    console.error('Error getting current conversation:', error);
    return null;
  }
};

/**
 * Clear current conversation selection
 */
export const clearCurrentConversation = () => {
  try {
    localStorage.removeItem(CURRENT_CONVERSATION_KEY);
  } catch (error) {
    console.error('Error clearing current conversation:', error);
  }
};

/**
 * Auto-save conversation during chat
 * @param {string} conversationId - Conversation ID
 * @param {Array} messages - Current messages array
 * @param {string} title - Conversation title
 */
export const autoSaveConversation = (conversationId, messages, title) => {
  try {
    if (!conversationId || !messages || messages?.length === 0) return;
    
    const conversation = {
      id: conversationId,
      title: title || generateConversationTitle(messages?.[0]?.content),
      messages: messages,
      messageCount: messages?.length
    };
    
    saveConversation(conversation);
  } catch (error) {
    console.error('Error auto-saving conversation:', error);
  }
};

/**
 * Clear all conversations (use with caution)
 * @returns {boolean} Success status
 */
export const clearAllConversations = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CURRENT_CONVERSATION_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing all conversations:', error);
    return false;
  }
};

/**
 * Export conversation to JSON
 * @param {string} conversationId - Conversation ID to export
 * @returns {string|null} JSON string or null if not found
 */
export const exportConversation = (conversationId) => {
  try {
    const conversation = getConversation(conversationId);
    if (!conversation) return null;
    
    return JSON.stringify(conversation, null, 2);
  } catch (error) {
    console.error('Error exporting conversation:', error);
    return null;
  }
};

/**
 * Import conversation from JSON
 * @param {string} jsonString - JSON string of conversation
 * @returns {Object|null} Imported conversation or null if failed
 */
export const importConversation = (jsonString) => {
  try {
    const conversation = JSON.parse(jsonString);
    
    // Generate new ID to avoid conflicts
    conversation.id = Date.now()?.toString();
    conversation.importedAt = new Date()?.toISOString();
    
    return saveConversation(conversation);
  } catch (error) {
    console.error('Error importing conversation:', error);
    return null;
  }
};